'use client';

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { memo, useCallback, useState, FormEvent } from "react";
import { cn } from "@/lib/utils";
import { ErrorDisplay, PasswordInput, useAuthError } from "./shared";

// Firebase Error interface
interface FirebaseError {
  code?: string;
  message?: string;
}

// Form data interfaces
interface LoginFormData {
  email: string;
  password: string;
}

interface SignupFormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
}

// Login Form Component
interface LoginFormProps {
  onSubmit: (data: LoginFormData) => Promise<void>;
  isLoading?: boolean;
  error?: FirebaseError | null;
  submitButtonText?: string;
  loadingText?: string;
}

export const LoginForm = memo<LoginFormProps>(({ 
  onSubmit, 
  isLoading = false, 
  error, 
  submitButtonText = "Log In",
  loadingText = "Signing in..."
}) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const errorInfo = useAuthError(error, 'login');

  const validateForm = useCallback(() => {
    const errors: Record<string, string> = {};
    
    if (!email) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = "Please enter a valid email address";
    }
    
    if (!password) {
      errors.password = "Password is required";
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }, [email, password]);

  const handleSubmit = useCallback(async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Prevent any default form behavior that might cause GET requests
    if (e.target && typeof (e.target as HTMLFormElement).submit === 'function') {
      (e.target as HTMLFormElement).submit = () => {};
    }
    
    if (!validateForm() || isLoading) {
      return;
    }

    try {
      await onSubmit({ email, password });
    } catch (error) {
      console.error('Form submission error:', error);
    }
  }, [email, password, validateForm, isLoading, onSubmit]);

  return (
    <form 
      onSubmit={handleSubmit} 
      method="post" 
      action="#"
      className="w-full min-w-[350px] space-y-8"
      style={{ minWidth: '350px', width: '100%' }}
    >
      {/* General Error Message */}
      {errorInfo?.isGeneralError && (
        <ErrorDisplay message={errorInfo.message} />
      )}

      {/* Email Field */}
      <div className="w-full min-w-[300px] space-y-3" style={{ minWidth: '300px' }}>
        <Label htmlFor="email" className="text-base font-medium">Email</Label>
        <Input
          id="email"
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isLoading}
          size="lg"
          className={cn(
            "w-full min-w-[300px] text-base",
            (fieldErrors.email || errorInfo?.isEmailError) && 'border-red-300 focus:border-red-500 focus:ring-red-500'
          )}
          style={{ minWidth: '300px', width: '100%' }}
        />
        {fieldErrors.email && (
          <p className="text-sm text-red-600">{fieldErrors.email}</p>
        )}
        {errorInfo?.isEmailError && (
          <ErrorDisplay message={errorInfo.message} />
        )}
      </div>

      {/* Password Field */}
      <div className="w-full min-w-[300px] space-y-3" style={{ minWidth: '300px' }}>
        <Label htmlFor="password" className="text-base font-medium">Password</Label>
        <PasswordInput
          id="password"
          value={password}
          onChange={setPassword}
          disabled={isLoading}
          hasError={!!(fieldErrors.password || errorInfo?.isPasswordError)}
          className={cn(
            "w-full min-w-[300px] text-base",
            (fieldErrors.password || errorInfo?.isPasswordError) && 'border-red-300 focus:border-red-500 focus:ring-red-500'
          )}
        />
        {fieldErrors.password && (
          <p className="text-sm text-red-600">{fieldErrors.password}</p>
        )}
        {errorInfo?.isPasswordError && (
          <ErrorDisplay message={errorInfo.message} />
        )}
      </div>

      {/* Submit Button */}
      <Button 
        type="submit" 
        disabled={isLoading}
        size="lg"
        className="w-full min-w-[300px] text-base font-medium"
        style={{ minWidth: '300px', width: '100%' }}
      >
        {isLoading ? (
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
            {loadingText}
          </div>
        ) : (
          submitButtonText
        )}
      </Button>
    </form>
  );
});

LoginForm.displayName = 'LoginForm';

// Signup Form Component
interface SignupFormProps {
  onSubmit: (data: SignupFormData) => Promise<void>;
  isLoading?: boolean;
  error?: FirebaseError | null;
  submitButtonText?: string;
  loadingText?: string;
}

export const SignupForm = memo<SignupFormProps>(({ 
  onSubmit, 
  isLoading = false, 
  error, 
  submitButtonText = "Sign Up",
  loadingText = "Creating account..."
}) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const errorInfo = useAuthError(error, 'signup');

  const validateForm = useCallback(() => {
    const errors: Record<string, string> = {};
    
    if (!firstName) {
      errors.firstName = "First name is required";
    }
    
    if (!lastName) {
      errors.lastName = "Last name is required";
    }
    
    if (!email) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = "Please enter a valid email address";
    }
    
    if (!password) {
      errors.password = "Password is required";
    } else if (password.length < 6) {
      errors.password = "Password must be at least 6 characters";
    }
    
    if (!confirmPassword) {
      errors.confirmPassword = "Please confirm your password";
    } else if (password !== confirmPassword) {
      errors.confirmPassword = "Passwords don't match";
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }, [firstName, lastName, email, password, confirmPassword]);

  const handleSubmit = useCallback(async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Prevent any default form behavior that might cause GET requests
    if (e.target && typeof (e.target as HTMLFormElement).submit === 'function') {
      (e.target as HTMLFormElement).submit = () => {};
    }
    
    if (!validateForm() || isLoading) {
      return;
    }

    try {
      await onSubmit({ firstName, lastName, email, password, confirmPassword });
    } catch (error) {
      console.error('Form submission error:', error);
    }
  }, [firstName, lastName, email, password, confirmPassword, validateForm, isLoading, onSubmit]);

  return (
    <form 
      onSubmit={handleSubmit} 
      method="post" 
      action="#"
      className="w-full min-w-[350px] space-y-8"
      style={{ minWidth: '350px', width: '100%' }}
    >
      {/* General Error Message */}
      {errorInfo?.isGeneralError && (
        <ErrorDisplay message={errorInfo.message} />
      )}

      {/* Name Fields Row */}
      <div className="grid grid-cols-2 gap-4 min-w-[300px]" style={{ minWidth: '300px' }}>
        <div className="space-y-2">
          <Label htmlFor="firstName" className="text-base font-medium">First Name</Label>
          <Input
            id="firstName"
            placeholder="John"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            disabled={isLoading}
            size="lg"
            className={cn(
              "w-full text-base",
              fieldErrors.firstName && 'border-red-300 focus:border-red-500 focus:ring-red-500/20'
            )}
          />
          {fieldErrors.firstName && (
            <ErrorDisplay message={fieldErrors.firstName} variant="field" />
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="lastName" className="text-base font-medium">Last Name</Label>
          <Input
            id="lastName"
            placeholder="Doe"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            disabled={isLoading}
            size="lg"
            className={cn(
              "w-full text-base",
              fieldErrors.lastName && 'border-red-300 focus:border-red-500 focus:ring-red-500/20'
            )}
          />
          {fieldErrors.lastName && (
            <ErrorDisplay message={fieldErrors.lastName} variant="field" />
          )}
        </div>
      </div>

      {/* Email Field */}
      <div className="w-full min-w-[300px] space-y-3" style={{ minWidth: '300px' }}>
        <Label htmlFor="signup-email" className="text-base font-medium">Email</Label>
        <Input
          id="signup-email"
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isLoading}
          size="lg"
          className={cn(
            "w-full min-w-[300px] text-base",
            (fieldErrors.email || errorInfo?.isEmailError) && 'border-red-300 focus:border-red-500 focus:ring-red-500'
          )}
          style={{ minWidth: '300px', width: '100%' }}
        />
        {fieldErrors.email && (
          <p className="text-sm text-red-600">{fieldErrors.email}</p>
        )}
        {errorInfo?.isEmailError && (
          <ErrorDisplay message={errorInfo.message} />
        )}
      </div>

      {/* Password Field */}
      <div className="w-full min-w-[300px] space-y-3" style={{ minWidth: '300px' }}>
        <Label htmlFor="signup-password" className="text-base font-medium">Password</Label>
        <PasswordInput
          id="signup-password"
          value={password}
          onChange={setPassword}
          disabled={isLoading}
          hasError={!!(fieldErrors.password || errorInfo?.isPasswordError)}
          className={cn(
            "w-full min-w-[300px] text-base",
            (fieldErrors.password || errorInfo?.isPasswordError) && 'border-red-300 focus:border-red-500 focus:ring-red-500'
          )}
        />
        {fieldErrors.password && (
          <p className="text-sm text-red-600">{fieldErrors.password}</p>
        )}
        {errorInfo?.isPasswordError && (
          <ErrorDisplay message={errorInfo.message} />
        )}
      </div>

      {/* Confirm Password Field */}
      <div className="w-full min-w-[300px] space-y-3" style={{ minWidth: '300px' }}>
        <Label htmlFor="signup-confirm-password" className="text-base font-medium">Confirm Password</Label>
        <PasswordInput
          id="signup-confirm-password"
          value={confirmPassword}
          onChange={setConfirmPassword}
          disabled={isLoading}
          hasError={!!fieldErrors.confirmPassword}
          className={cn(
            "w-full min-w-[300px] text-base",
            fieldErrors.confirmPassword && 'border-red-300 focus:border-red-500 focus:ring-red-500'
          )}
        />
        {fieldErrors.confirmPassword && (
          <p className="text-sm text-red-600">{fieldErrors.confirmPassword}</p>
        )}
      </div>

      {/* Submit Button */}
      <Button 
        type="submit" 
        disabled={isLoading}
        size="lg"
        className="w-full min-w-[300px] text-base font-medium"
        style={{ minWidth: '300px', width: '100%' }}
      >
        {isLoading ? (
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
            {loadingText}
          </div>
        ) : (
          submitButtonText
        )}
      </Button>
    </form>
  );
});

SignupForm.displayName = 'SignupForm';
